"use client";

import React, { useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  RefreshCw,
  Home,
  Compass,
  HelpCircle,
  MapPinOff,
  ServerCrash,
  ShieldAlert,
  LockKeyhole,
  ShieldX,
  WifiOff,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  LucideIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ErrorViewProps {
  code?: string | number;
  title?: string;
  description?: string;
  error?: Error & { digest?: string };
  reset?: () => void;
}

interface ErrorConfig {
  icon: LucideIcon;
  title: string;
  description: string;
  badgeColor: string;
  glowColor: string;
}

export function ErrorView({
  code = "500",
  title: overrideTitle,
  description: overrideDescription,
  error,
  reset,
}: ErrorViewProps) {
  const [copied, setCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const errorConfigs: Record<string, ErrorConfig> = {
    "400": {
      icon: ShieldAlert,
      title: "Bad Request",
      description:
        "The counseling predictor couldn't parse your request. Please check your inputs or filters and try again.",
      badgeColor: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
      glowColor: "bg-amber-500/10 dark:bg-amber-500/15",
    },
    "401": {
      icon: LockKeyhole,
      title: "Unauthorized Access",
      description:
        "You need to be authenticated to view this counseling summary or profile data.",
      badgeColor: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
      glowColor: "bg-indigo-500/10 dark:bg-indigo-500/15",
    },
    "403": {
      icon: ShieldX,
      title: "Access Forbidden",
      description:
        "You don't have authorization to view this seat matrix configuration or private record.",
      badgeColor: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
      glowColor: "bg-rose-500/10 dark:bg-rose-500/15",
    },
    "404": {
      icon: MapPinOff,
      title: "Path Lost in Transition",
      description:
        "The page or counselor route you are trying to access doesn't exist, has moved, or has been updated.",
      badgeColor: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      glowColor: "bg-blue-500/10 dark:bg-blue-500/15",
    },
    "500": {
      icon: ServerCrash,
      title: "Internal Server Error",
      description:
        "Our seat allocation prediction algorithms hit an unexpected runtime exception. We are debugging it now.",
      badgeColor: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      glowColor: "bg-red-500/10 dark:bg-red-500/15",
    },
    "503": {
      icon: WifiOff,
      title: "Service Temporarily Offline",
      description:
        "The prediction engine is currently undergoing maintenance. Historical cutoff lists will resume shortly.",
      badgeColor: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
      glowColor: "bg-orange-500/10 dark:bg-orange-500/15",
    },
  };

  const codeString = code.toString();
  const config = errorConfigs[codeString] || {
    icon: AlertCircle,
    title: "Unexpected Error",
    description: "An unexpected error occurred while loading this section.",
    badgeColor: "bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300",
    glowColor: "bg-primary/10 dark:bg-primary/15",
  };

  const IconComponent = config.icon;
  const displayTitle = overrideTitle || config.title;
  const displayDescription = overrideDescription || config.description;

  const handleCopyError = () => {
    if (!error) return;
    const text = `Error: ${error.message}\nDigest: ${error.digest || "N/A"}\nStack: ${error.stack || "N/A"}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative flex-1 flex flex-col items-center justify-center min-h-[75vh] px-4 py-16 sm:px-6 lg:px-8">
      {/* Decorative Blur Background */}
      <div
        className={cn(
          "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-87.5 sm:w-125 h-87.5 sm:h-125 rounded-full blur-[100px] -z-15 transition-colors duration-500",
          config.glowColor
        )}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-xl text-center"
      >
        {/* Status Badge */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-surface-container border border-border/40 text-on-surface shadow-sm mb-6">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
          STATUS CODE: {code}
        </div>

        {/* Floating Animated Icon */}
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{
            repeat: Infinity,
            duration: 5,
            ease: "easeInOut",
          }}
          className="relative flex items-center justify-center mx-auto size-24 sm:size-28 rounded-3xl bg-surface-container-high dark:bg-surface-container/50 border border-border/20 shadow-lg mb-8"
        >
          {/* Inner ring pulse */}
          <div className="absolute inset-0 rounded-3xl border border-primary/20 scale-105 animate-pulse" />
          <IconComponent className="size-12 sm:size-14 text-primary dark:text-primary-container" />
        </motion.div>

        {/* Heading */}
        <h1 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-foreground mb-4">
          {displayTitle}
        </h1>

        {/* Description */}
        <p className="text-base sm:text-lg text-on-surface-variant max-w-md mx-auto mb-8 font-medium leading-relaxed">
          {displayDescription}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
          {reset && (
            <Button
              onClick={() => reset()}
              variant="default"
              size="lg"
              className="w-full sm:w-auto font-semibold gap-2 border border-primary/10"
            >
              <RefreshCw className="size-4 animate-spin-hover group-hover/button:rotate-180 transition-transform duration-500" />
              Try Reconnecting
            </Button>
          )}
          <Button
            asChild
            variant={reset ? "outline" : "default"}
            size="lg"
            className="w-full sm:w-auto font-semibold gap-2"
          >
            <Link href="/predictor">
              <Compass className="size-4" />
              CCMT College Predictor
            </Link>
          </Button>
          {!reset && (
            <Button
              asChild
              variant="outline"
              size="lg"
              className="w-full sm:w-auto font-semibold gap-2"
            >
              <Link href="/">
                <Home className="size-4" />
                Return to Home
              </Link>
            </Button>
          )}
        </div>

        {/* Developer Console / Advanced error details (collapsible) */}
        {error && (
          <div className="w-full text-left bg-surface-container-low/55 dark:bg-surface-container-low/25 border border-border/20 dark:border-outline-variant/10 rounded-2xl overflow-hidden transition-all duration-300 shadow-sm mb-8">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full flex items-center justify-between px-5 py-4 text-sm font-bold text-on-surface-variant hover:text-foreground hover:bg-surface-container-high/40 transition-colors"
            >
              <span className="flex items-center gap-2">
                <AlertCircle className="size-4 text-destructive" />
                Developer Technical Diagnostics
              </span>
              {showDetails ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </button>

            {showDetails && (
              <div className="px-5 pb-5 pt-1 border-t border-border/10">
                <div className="relative group">
                  <pre className="p-4 bg-zinc-950 dark:bg-black rounded-xl text-xs text-red-400 font-mono overflow-x-auto max-h-48 leading-5 shadow-inner">
                    <code>
                      {`Error: ${error.message}\n`}
                      {error.digest && `Digest ID: ${error.digest}\n`}
                      {error.stack && `Stack trace:\n${error.stack}`}
                    </code>
                  </pre>
                  <button
                    onClick={handleCopyError}
                    className="absolute top-3 right-3 p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                    title="Copy diagnostics to clipboard"
                  >
                    {copied ? (
                      <Check className="size-3.5 text-emerald-400" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </button>
                </div>
                <p className="mt-3 text-2xs text-on-surface-variant/75 font-semibold uppercase tracking-wider text-center">
                  Copy and send these logs to counseling support to isolate the issue.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Helpful links grid */}
        <div className="border-t border-border/10 pt-8">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-4">
            Useful Links & Resources
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Link
              href="/explorer"
              className="flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-surface-container-low hover:bg-surface-container-high text-on-surface hover:text-primary font-bold shadow-2xs transition-all"
            >
              <Compass className="size-4 text-primary" />
              <span>College Explorer</span>
            </Link>
            <Link
              href="/faq"
              className="flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-surface-container-low hover:bg-surface-container-high text-on-surface hover:text-primary font-bold shadow-2xs transition-all"
            >
              <HelpCircle className="size-4 text-primary" />
              <span>Counseling FAQ</span>
            </Link>
            <Link
              href="/contact"
              className="col-span-2 sm:col-span-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-surface-container-low hover:bg-surface-container-high text-on-surface hover:text-primary font-bold shadow-2xs transition-all"
            >
              <ArrowLeft className="size-4 text-primary" />
              <span>Contact Us</span>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
